import { createSlice } from '@reduxjs/toolkit';
import {
    getAssociatedDocumentListQuery,
    addAssociatedDocumentThunk,
    getAssociatedDocumentThunk,
    updateAssociatedDocumentThunk,
    deleteAssociatedDocumentThunk,
    downloadAssociatedDocumentThunk,
} from './thunk';

export const initialState = {
    data: [],
    dataToEdit: null,
    error: null,
    isListLoading: false,
    isListLoaded: false,
    isGetting: false,
    isGot: false,
    isAdding: false,
    isAdded: false,
    isUpdating: false,
    isUpdated: false,
    isDeleting: false,
    isDeleted: false,
    isDownloading: false,
    isDownloaded: false,
    downloadedUrl: null,
    downloadedFilename: null,
    isCloseModal: false,
    requireRefetch: false,
};

const associatedDocumentSlice = createSlice({
    name: 'associatedDocument',
    initialState,
    reducers: {
        resetAssociatedDocumentFlags: (state) => {
            state.isAdded = false;
            state.isUpdated = false;
            state.isDeleted = false;
            state.isDownloaded = false;
            state.isCloseModal = false;
        },
        setRequireRefetch: (state, action) => {
            state.requireRefetch = action.payload;
        },
        setDataToEdit: (state, action) => {
            state.dataToEdit = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Get List
        builder.addCase(getAssociatedDocumentListQuery.pending, (state) => {
            state.isListLoading = true;
            state.isListLoaded = false;
        });
        builder.addCase(getAssociatedDocumentListQuery.fulfilled, (state, action) => {
            state.data = action.payload?.data || [];
            state.isListLoading = false;
            state.isListLoaded = true;
        });
        builder.addCase(getAssociatedDocumentListQuery.rejected, (state, action) => {
            state.error = action.payload?.error || null;
            state.isListLoading = false;
            state.isListLoaded = false;
        });

        // Add
        builder.addCase(addAssociatedDocumentThunk.pending, (state) => {
            state.isAdding = true;
            state.isAdded = false;
        });
        builder.addCase(addAssociatedDocumentThunk.fulfilled, (state) => {
            state.isAdding = false;
            state.isAdded = true;
            state.isCloseModal = true;
            state.requireRefetch = true;
        });
        builder.addCase(addAssociatedDocumentThunk.rejected, (state, action) => {
            state.error = action.payload?.error || null;
            state.isAdding = false;
            state.isAdded = false;
        });

        // Get by ID
        builder.addCase(getAssociatedDocumentThunk.pending, (state) => {
            state.isGetting = true;
            state.isGot = false;
        });
        builder.addCase(getAssociatedDocumentThunk.fulfilled, (state, action) => {
            state.dataToEdit = action.payload;
            state.isGetting = false;
            state.isGot = true;
        });
        builder.addCase(getAssociatedDocumentThunk.rejected, (state, action) => {
            state.error = action.payload?.error || null;
            state.isGetting = false;
            state.isGot = false;
        });

        // Update
        builder.addCase(updateAssociatedDocumentThunk.pending, (state) => {
            state.isUpdating = true;
            state.isUpdated = false;
        });
        builder.addCase(updateAssociatedDocumentThunk.fulfilled, (state) => {
            state.isUpdating = false;
            state.isUpdated = true;
            state.isCloseModal = true;
            state.requireRefetch = true;
        });
        builder.addCase(updateAssociatedDocumentThunk.rejected, (state, action) => {
            state.error = action.payload?.error || null;
            state.isUpdating = false;
            state.isUpdated = false;
        });

        // Delete
        builder.addCase(deleteAssociatedDocumentThunk.pending, (state) => {
            state.isDeleting = true;
            state.isDeleted = false;
        });
        builder.addCase(deleteAssociatedDocumentThunk.fulfilled, (state) => {
            state.isDeleting = false;
            state.isDeleted = true;
            state.isCloseModal = true;
            state.requireRefetch = true;
        });
        builder.addCase(deleteAssociatedDocumentThunk.rejected, (state, action) => {
            state.error = action.payload?.error || null;
            state.isDeleting = false;
            state.isDeleted = false;
        });

        // Download
        builder.addCase(downloadAssociatedDocumentThunk.pending, (state) => {
            state.isDownloading = true;
            state.isDownloaded = false;
            state.downloadedUrl = null;
            state.downloadedFilename = null;
        });
        builder.addCase(downloadAssociatedDocumentThunk.fulfilled, (state, action) => {
            state.isDownloading = false;
            state.isDownloaded = true;
            state.downloadedUrl = action.payload.url;
            state.downloadedFilename = action.payload.filename;
        });
        builder.addCase(downloadAssociatedDocumentThunk.rejected, (state, action) => {
            state.error = action.payload?.error || null;
            state.isDownloading = false;
            state.isDownloaded = false;
        });
    },
});

export const { resetAssociatedDocumentFlags, setRequireRefetch, setDataToEdit } =
    associatedDocumentSlice.actions;

export default associatedDocumentSlice.reducer;
